<template lang="pug">
.tag-manager-container
  .tag-manager-header
    h3 {{$t('Tag Manager')}}
  el-tabs(v-model='activeTab', @tab-click='handleTabSwitch')
    el-tab-pane(:label='$t("Device Tags")', name='device')
    el-tab-pane(:label='$t("Media Tags")', name='media')
  .tag-bar
    .tag-bar-header
      el-input.search-input(v-model='tagSearch', size='mini', :placeholder='$t("Search tags...")', clearable, prefix-icon='el-icon-search')
      el-button(v-if='!showCreateInput', size='small', type='success', plain, @click='showCreateTagInput') {{$t('+ Create Tag')}}
      el-input.create-input(v-if='showCreateInput', v-model='newTagName', ref='newTagInput', size='mini', @keyup.enter.native='createTag', @blur='createTag', :placeholder='$t("New tag name")')
    .tag-list
      .tag-item(
        v-for='tag in filteredTags',
        :key='tag.id',
        :class='{ selected: selectedTag && selectedTag.id === tag.id }',
        @click='selectTag(tag)'
      )
        span.tag-name {{ tag.name }}
        el-badge.tag-count(:value='getItemCount(tag)', type='info')
      .no-tags(v-if='filteredTags.length === 0')
        span.muted {{ tagSearch ? $t('No tags matching search.') : $t('No tags in network.') }}
  .tag-detail(v-if='selectedTag')
    .detail-header
      .detail-header-left
        el-input.tag-name-edit(
          v-if='editingTagName',
          v-model='editedTagName',
          size='small',
          ref='tagNameInput',
          @keyup.enter.native='saveTagRename',
          @keyup.esc.native='editingTagName = false'
        )
        h4(v-else) {{ selectedTag.name }}
      el-button-group
        el-button(size='mini', @click='startRenameTag', :disabled='editingTagName || processing')
          font-awesome-icon(icon='edit')
          span &nbsp; {{$t('Rename')}}
        el-button(size='mini', type='danger', @click='deleteTag', :disabled='processing')
          font-awesome-icon(icon='trash')
          span &nbsp; {{$t('Delete from network')}}
    .detail-content
      el-transfer.tag-transfer(
        v-model='assignedItemIds',
        :data='transferData',
        :titles='transferTitles',
        :filter-placeholder='$t("Search...")',
        filterable,
        @change='handleTransferChange',
        :props='{ key: "key", label: "label" }'
      )
    .processing-overlay(v-if='processing')
      font-awesome-icon.fa-spin(icon='spinner', size='2x')
      span &nbsp; {{ $t('Processing...') }} {{ percentDone }}%
  .tag-detail-empty(v-if='!selectedTag')
    p.muted {{$t('Select a tag from the list to manage its assignments.')}}
</template>

<script>
import userUtils from './userUtils.mixin'

export default {
  mixins: [userUtils],

  async created () {
    await this.loadData()
  },

  data () {
    return {
      activeTab: 'device',
      selectedTag: undefined,
      tagSearch: '',
      showCreateInput: false,
      newTagName: '',
      editingTagName: false,
      editedTagName: '',
      assignedItemIds: [],
      processing: false,
      percentDone: 0
    }
  },

  computed: {
    allTags () {
      if (this.activeTab === 'device') {
        const used = this.$store.state.Device.device_tags.map(t => ({
          ...t,
          itemCount: t.device ? t.device.length : 0
        }))
        const unused = this.$store.state.Device.device_tags_unused.map(t => ({
          ...t,
          itemCount: 0,
          device: []
        }))
        const merged = [...used]
        for (const u of unused) {
          if (!merged.find(m => m.id === u.id)) merged.push(u)
        }
        return merged.sort((a, b) => a.name.localeCompare(b.name))
      } else {
        const used = this.$store.state.Medium.media_tags.map(t => ({
          ...t,
          itemCount: t.media ? t.media.length : 0
        }))
        const unused = this.$store.state.Medium.media_tags_unused.map(t => ({
          ...t,
          itemCount: 0,
          media: []
        }))
        const merged = [...used]
        for (const u of unused) {
          if (!merged.find(m => m.id === u.id)) merged.push(u)
        }
        return merged.sort((a, b) => a.name.localeCompare(b.name))
      }
    },

    filteredTags () {
      if (!this.tagSearch) return this.allTags
      const search = this.tagSearch.toLowerCase()
      return this.allTags.filter(t => t.name.toLowerCase().includes(search))
    },

    transferData () {
      if (this.activeTab === 'device') {
        return this.$store.state.Device.device_list.map(d => ({
          key: d.id,
          label: d.name
        }))
      } else {
        return this.$store.state.Medium.media_list
          .filter(m => m.type !== 'PlayListDesktop' && m.type !== 'PlayListLibraryRoot')
          .map(m => ({
            key: m.id,
            label: m.name + ' (' + m.type + ')'
          }))
      }
    },

    transferTitles () {
      if (this.activeTab === 'device') {
        return [this.$t('Available devices'), this.$t('Assigned to tag')]
      } else {
        return [this.$t('Available media'), this.$t('Assigned to tag')]
      }
    }
  },

  methods: {
    async loadData () {
      await Promise.all([
        this.$store.dispatch('Device/setDeviceTags'),
        this.$store.dispatch('Device/setUnusedTags'),
        this.$store.dispatch('Medium/setMediaTags')
      ])
    },

    handleTabSwitch () {
      this.selectedTag = undefined
      this.assignedItemIds = []
      this.tagSearch = ''
      this.editingTagName = false
      this.showCreateInput = false
    },

    selectTag (tag) {
      this.selectedTag = tag
      this.editingTagName = false
      if (this.activeTab === 'device') {
        this.assignedItemIds = tag.device ? [...tag.device] : []
      } else {
        this.assignedItemIds = tag.media ? [...tag.media] : []
      }
    },

    async handleTransferChange (newAssignedIds, direction, movedKeys) {
      this.processing = true
      this.percentDone = 0
      const total = movedKeys.length
      let done = 0
      try {
        for (const itemId of movedKeys) {
          if (direction === 'right') {
            if (this.activeTab === 'device') {
              await this.$store.dispatch('Device/addDeviceTagToDevice', [this.selectedTag.id, itemId])
            } else {
              await this.$store.dispatch('Medium/addMediumTagToMedium', [this.selectedTag.id, itemId])
            }
          } else {
            if (this.activeTab === 'device') {
              await this.$store.dispatch('Device/deleteDeviceTagFromDevice', [this.selectedTag.id, itemId])
            } else {
              await this.$store.dispatch('Medium/deleteMediumTagFromMedium', [this.selectedTag.id, itemId])
            }
          }
          done++
          this.percentDone = parseInt(done / total * 100)
        }
        await this.refreshTags()
        const refreshedTag = this.allTags.find(t => t.id === this.selectedTag.id)
        if (refreshedTag) {
          this.selectTag(refreshedTag)
        }
      } finally {
        this.processing = false
        this.percentDone = 0
      }
    },

    async refreshTags () {
      if (this.activeTab === 'device') {
        await this.$store.dispatch('Device/setDeviceTags')
        await this.$store.dispatch('Device/setUnusedTags')
      } else {
        await this.$store.dispatch('Medium/setMediaTags')
      }
    },

    showCreateTagInput () {
      this.showCreateInput = true
      this.$nextTick(() => {
        this.$refs.newTagInput.$refs.input.focus()
      })
    },

    async createTag () {
      if (!this.newTagName || this.newTagName.trim().length === 0) {
        this.showCreateInput = false
        this.newTagName = ''
        return
      }
      if (this.activeTab === 'device') {
        await this.$store.dispatch('Device/addDeviceTag', this.newTagName.trim())
        await this.$store.dispatch('Device/setUnusedTags')
      } else {
        await this.$store.dispatch('Medium/addMediumTag', this.newTagName.trim())
        await this.$store.dispatch('Medium/setMediaTags')
      }
      this.newTagName = ''
      this.showCreateInput = false
    },

    startRenameTag () {
      this.editedTagName = this.selectedTag.name
      this.editingTagName = true
      this.$nextTick(() => {
        this.$refs.tagNameInput.$refs.input.focus()
      })
    },

    async saveTagRename () {
      if (!this.editedTagName || this.editedTagName === this.selectedTag.name) {
        this.editingTagName = false
        return
      }
      const tagToSave = { id: this.selectedTag.id, name: this.editedTagName }
      if (this.activeTab === 'device') {
        await this.$store.dispatch('Device/saveDeviceTagInNetwork', tagToSave)
        await this.$store.dispatch('Device/setDeviceTags')
        await this.$store.dispatch('Device/setUnusedTags')
      } else {
        await this.$store.dispatch('Medium/saveMediumTagInNetwork', tagToSave)
      }
      this.editingTagName = false
      const refreshedTag = this.allTags.find(t => t.id === this.selectedTag.id)
      if (refreshedTag) this.selectTag(refreshedTag)
    },

    async deleteTag () {
      try {
        await this.$confirm(
          this.$t('Are you sure you want to delete this tag from the network?'),
          this.$t('Confirm'),
          {
            confirmButtonText: this.$t('Delete'),
            cancelButtonText: this.$t('Cancel'),
            type: 'warning'
          }
        )
      } catch (e) {
        return
      }
      if (this.activeTab === 'device') {
        await this.$store.dispatch('Device/deleteDeviceTagFromNetwork', this.selectedTag.id)
      } else {
        await this.$store.dispatch('Medium/deleteMediumTagFromNetwork', this.selectedTag.id)
      }
      this.selectedTag = undefined
      this.assignedItemIds = []
    },

    getItemCount (tag) {
      if (this.activeTab === 'device') {
        return tag.device ? tag.device.length : 0
      } else {
        return tag.media ? tag.media.length : 0
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../theme-m4b/src/common/var.scss";

.tag-manager-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tag-manager-header {
  padding: 0 10px;
  h3 {
    margin: 0 0 10px 0;
  }
}

.tag-bar {
  display: flex;
  align-items: flex-start;
  padding: 0 10px 10px 10px;
  border-bottom: 1px solid $--border-color-lighter;
  flex-wrap: wrap;
  gap: 4px;

  .tag-bar-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: 12px;
    .search-input {
      width: 180px;
    }
    .create-input {
      width: 150px;
    }
  }

  .tag-list {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    flex: 1;
  }

  .tag-item {
    padding: 4px 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid $--border-color-lighter;
    border-radius: 4px;
    color: $--color-text-secondary;
    font-size: 13px;
    white-space: nowrap;

    &.selected {
      background-color: rgba($--color-primary, 0.6);
      color: #fff;
      border-color: $--color-primary;
      font-weight: bold;
    }

    &:hover:not(.selected) {
      background-color: rgba($--color-primary, 0.08);
    }

    .tag-name {
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 200px;
    }
  }

  .no-tags {
    padding: 4px 0;
    .muted {
      color: $--color-text-secondary;
    }
  }
}

.tag-detail {
  flex: 1;
  padding: 10px 20px;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;

  .detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid $--border-color-lighter;

    .detail-header-left {
      flex: 1;
      margin-right: 10px;
      h4 {
        margin: 0;
      }
    }

    .tag-name-edit {
      max-width: 300px;
    }
  }

  .detail-content {
    flex: 1;
    display: flex;
    min-height: 0;
  }
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  z-index: 10;
}

.tag-detail-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  .muted {
    color: $--color-text-secondary;
    font-size: 14px;
  }
}

.tag-transfer {
  width: 100%;
}
</style>

<style lang="scss">
.tag-manager-container {
  .tag-transfer.el-transfer {
    display: flex;
    width: 100%;
  }

  .tag-transfer .el-transfer-panel {
    width: 40%;
  }

  .tag-transfer .el-transfer-panel .el-transfer-panel__body {
    height: calc(100vh - 300px);
  }

  .tag-transfer .el-transfer-panel .el-transfer-panel__list {
    height: calc(100vh - 350px);
    overflow-y: auto;
  }

  .tag-transfer .el-transfer__buttons {
    flex-shrink: 0;
    padding: 0 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Common/TagManager.vue